import { capabilities } from "../capabilities";
import config from "./config";

const isIntercomAllowed = () => capabilities.allowed("intercom");

const disabled = (operation: string) => {
  console.log(`::intercom web:: ${operation} disabled in preview`);
};

type IntercomSettings = {
  app_id: string;
  user_id?: string;
  email?: string;
  hide_default_launcher?: boolean;
};

type IntercomCommand = "boot" | "shutdown" | "show" | "update";

type IntercomFunction = {
  (command: IntercomCommand, settings?: IntercomSettings): void;
  q?: IArguments[];
  c?: (args: IArguments) => void;
};

declare global {
  interface Window {
    Intercom?: IntercomFunction;
    intercomSettings?: IntercomSettings;
  }
}

let loadPromise: Promise<void> | undefined;
let bootedIdentity: string | undefined;

const isBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

const getAppId = () => config.INTERCOM.APP_ID;

const getIdentity = (settings: IntercomSettings) =>
  settings.user_id || settings.email || "anonymous";

const installIntercomStub = () => {
  if (!isBrowser() || window.Intercom) {
    return;
  }

  const intercom = function intercomStub() {
    intercom.q?.push(arguments);
  } as IntercomFunction;

  intercom.q = [];
  intercom.c = (args) => intercom.q?.push(args);
  window.Intercom = intercom;
};

const loadIntercom = async (appId: string) => {
  if (!isBrowser()) {
    return;
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    installIntercomStub();

    if (document.querySelector(`script[data-intercom-app-id="${appId}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    script.dataset.intercomAppId = appId;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Intercom widget"));
    document.head.appendChild(script);
  });

  return loadPromise;
};

const ensureBooted = async (settings: IntercomSettings) => {
  if (!isIntercomAllowed()) {
    disabled("boot");
    return false;
  }

  if (!isBrowser()) {
    return false;
  }

  const appId = getAppId();

  if (!appId) {
    console.log("::intercom web:: missing EXPO_PUBLIC_INTERCOM_APP_ID");
    return false;
  }

  await loadIntercom(appId);

  const identity = getIdentity(settings);

  if (bootedIdentity && bootedIdentity !== identity) {
    window.Intercom?.("shutdown");
    bootedIdentity = undefined;
  }

  window.intercomSettings = settings;

  if (bootedIdentity) {
    window.Intercom?.("update", settings);
  } else {
    window.Intercom?.("boot", settings);
    bootedIdentity = identity;
  }

  return true;
};

export const loginUnidentifiedUser = async () => {
  if (!isIntercomAllowed()) {
    disabled("loginUnidentifiedUser");
    return;
  }

  const appId = getAppId();

  await ensureBooted({
    app_id: appId,
    hide_default_launcher: true,
  });
};

export const loginUserWithUserAttributes = async (attributes: {
  userId?: string;
  email?: string;
}) => {
  if (!isIntercomAllowed()) {
    disabled("loginUserWithUserAttributes");
    return;
  }

  const appId = getAppId();

  await ensureBooted({
    app_id: appId,
    user_id: attributes.userId,
    email: attributes.email,
    hide_default_launcher: true,
  });
};

export const logout = async () => {
  if (!isBrowser()) {
    return;
  }

  window.Intercom?.("shutdown");
  bootedIdentity = undefined;
  window.intercomSettings = undefined;
};

export const present = async () => {
  if (!isIntercomAllowed()) {
    disabled("present");
    return;
  }

  const booted =
    bootedIdentity !== undefined ||
    (await ensureBooted({
      app_id: getAppId(),
      hide_default_launcher: true,
    }));

  if (booted) {
    window.Intercom?.("show");
  }
};
